/**
 * Fetch/refresh dynamic body fields from a field's schema.perform (e.g. Sheets columns).
 * Preserves any non-empty values the user already mapped.
 */
import axios from 'axios';
import {
  unwrapFieldValue,
  resolveParamValueFromFields,
  getUnmetDynamicParams,
  isFieldValuePresent,
} from './dynamicFieldDeps';

const DJANGO_CALLER =
  'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller';

export function findPerformSourceField(fields) {
  return (fields || []).find((f) => f?.schema?.perform && typeof f.schema.perform === 'string');
}

/** True when a perform source field exists and its parent params (and own value) are set. */
export function canRefreshPerformFields(node) {
  const fields = node?.data?.fields || [];
  const source = findPerformSourceField(fields);
  if (!source) return false;
  if (node?.data?.hasauth && !node?.data?.credential) return false;
  if (!isFieldValuePresent(source.value)) return false;
  return getUnmetDynamicParams(source, fields).length === 0;
}

/** Union schema properties: keep existing keys, add/update from perform response. */
export function deepMergeSchemaProperties(prev, next) {
  const out = { ...(prev || {}) };
  Object.keys(next || {}).forEach((key) => {
    if (key === '') return;
    const n = next[key];
    const p = prev?.[key];
    if (n?.type === 'object' && n.properties) {
      out[key] = {
        ...(p || {}),
        ...n,
        properties: deepMergeSchemaProperties(p?.properties || {}, n.properties),
      };
    } else {
      out[key] = { ...(p || {}), ...n };
    }
  });
  return out;
}

/**
 * Keep existing mapped values; never invent blank values for new fields.
 */
export function preservePerformValues(prevValue, schemaProps) {
  const prev =
    prevValue && typeof prevValue === 'object' && !Array.isArray(prevValue) ? prevValue : {};
  const out = { ...prev };

  Object.keys(schemaProps || {}).forEach((key) => {
    if (key === '') return;
    const prop = schemaProps[key];
    if (prop?.type === 'object' && prop.properties) {
      const nestedPrev = prev[key];
      if (nestedPrev && typeof nestedPrev === 'object' && !Array.isArray(nestedPrev)) {
        out[key] = preservePerformValues(nestedPrev, prop.properties);
      }
    }
  });

  return out;
}

/**
 * Merge newly built perform schema.properties into the existing body field.
 * Updates schema (add/refresh keys) while preserving existing values.
 */
export function mergePerformBody(existingBody, newSchemaProperties) {
  const prevSchema = existingBody?.schema?.properties || {};
  const prevValue =
    existingBody?.value && typeof existingBody.value === 'object' && !Array.isArray(existingBody.value)
      ? existingBody.value
      : {};
  const mergedSchemaProps = deepMergeSchemaProperties(prevSchema, newSchemaProperties);
  const mergedValue = preservePerformValues(prevValue, mergedSchemaProps);

  return {
    ...(existingBody || {
      description: ' ',
      in: 'path',
      name: 'body',
      title: ' ',
    }),
    schema: {
      ...(existingBody?.schema || {}),
      properties: mergedSchemaProps,
    },
    value: mergedValue,
  };
}

function buildChoicesProperties(responseData, performDataHead, performKey, performValue) {
  const rows =
    performDataHead === '0' || performDataHead == null
      ? Array.isArray(responseData)
        ? responseData
        : []
      : Array.isArray(responseData?.[performDataHead])
        ? responseData[performDataHead]
        : [];
  return rows
    .map((choice) => ({
      name: choice[performKey],
      title: choice[performValue],
    }))
    .filter((c) => c.name != null && c.name !== '');
}

/**
 * Build the body.schema.properties object the same way as sendDynamicFormSignal.
 */
export function buildPerformSchemaProperties(
  choicesProperties,
  fieldTitle,
  performDataHead,
  performPropertyName
) {
  if (performDataHead === '0') {
    const properties = {};
    choicesProperties.forEach((choice) => {
      properties[choice.name] = {
        description: choice.title,
        title: choice.title,
        type: 'string',
      };
    });
    // Legacy shape: schema.properties is itself an object-schema
    return {
      description: fieldTitle,
      title: fieldTitle,
      type: 'object',
      properties,
    };
  }

  if (performPropertyName === '0') {
    const properties = {};
    choicesProperties.forEach((choice) => {
      properties[choice.name] = {
        description: choice.title,
        title: choice.title,
        type: 'string',
      };
    });
    return properties;
  }

  const groupKey = performPropertyName || performDataHead;
  const properties = {};
  choicesProperties.forEach((choice) => {
    properties[choice.name] = {
      description: choice.title,
      title: choice.title,
      type: 'string',
    };
  });
  return {
    [groupKey]: {
      description: fieldTitle,
      title: fieldTitle,
      type: 'object',
      properties,
    },
  };
}

/**
 * Merge perform schema into body for the legacy perform_data_head === '0' shape
 * where schema.properties is { description, title, type, properties: { leaves } }.
 */
function mergeLegacyPerformBody(existingBody, newRootObjectSchema) {
  const prevRoot = existingBody?.schema?.properties || {};
  const prevInner = prevRoot?.properties || {};
  const nextInner = newRootObjectSchema?.properties || {};
  const mergedInner = deepMergeSchemaProperties(prevInner, nextInner);
  const mergedRoot = {
    ...prevRoot,
    ...newRootObjectSchema,
    properties: mergedInner,
  };
  const prevValue =
    existingBody?.value && typeof existingBody.value === 'object' && !Array.isArray(existingBody.value)
      ? existingBody.value
      : {};
  return {
    ...(existingBody || { description: ' ', in: 'path', name: 'body', title: ' ' }),
    schema: { ...(existingBody?.schema || {}), properties: mergedRoot },
    value: preservePerformValues(prevValue, mergedInner),
  };
}

/**
 * Re-run perform for the node and merge results into body without wiping mapped values.
 * @returns {Promise<object[]>} updated fields array
 */
export async function refreshPerformFields(node, { dispatch, loadingperform, elementUpdated, saveWorkflow }) {
  const data = node?.data || {};
  const fields = Array.isArray(data.fields) ? [...data.fields] : [];
  const source = findPerformSourceField(fields);
  if (!source?.schema?.perform) {
    throw new Error('No perform field on this step');
  }
  if (!canRefreshPerformFields(node)) {
    throw new Error('Select required fields before refreshing');
  }

  const performSplit = String(source.schema.perform).split('.');
  const dynamicApp = performSplit[0];
  const fieldTitle = performSplit[1];
  const performAction = performSplit[2];
  const performDataHead = performSplit[3];
  const performKey = performSplit[4];
  const performValue = performSplit[5];
  const performPropertyName = performSplit[6];
  const params = source.schema.params || [];

  const replacedParams = params
    .map((param) => {
      if (!param?.name) return null;
      let paramValue =
        param.name === source.name
          ? unwrapFieldValue(source.value)
          : resolveParamValueFromFields(fields, param.name);
      if (paramValue === undefined) return null;
      return { name: param.name, in: 'path', value: paramValue };
    })
    .filter(Boolean);

  if (dispatch && loadingperform) dispatch(loadingperform(true));

  try {
    const response = await axios({
      method: 'post',
      url: DJANGO_CALLER,
      headers: { 'Content-Type': 'application/json' },
      data: {
        operation: 'getDD',
        data: {
          url: data.url,
          app: dynamicApp,
          credential: data.credential,
          action: performAction,
          fields: replacedParams,
        },
      },
    });

    const choicesProperties = buildChoicesProperties(
      response.data,
      performDataHead,
      performKey,
      performValue
    );

    const newSchemaProperties = buildPerformSchemaProperties(
      choicesProperties,
      fieldTitle,
      performDataHead,
      performPropertyName
    );

    const existingBody = fields.find((f) => f.name === 'body' || f.in === 'formData');
    const mergedBody =
      performDataHead === '0'
        ? mergeLegacyPerformBody(existingBody, newSchemaProperties)
        : mergePerformBody(existingBody, newSchemaProperties);

    const nextFields = (() => {
      const hasBody = fields.some((f) => f.name === 'body' || f.in === 'formData');
      if (!hasBody) return [...fields, mergedBody];
      return fields.map((f) => (f.name === 'body' || f.in === 'formData' ? mergedBody : f));
    })();

    if (dispatch && elementUpdated) {
      dispatch(
        elementUpdated(
          node.id,
          {
            ...data,
            type: data.app || data.type || '',
            fields: nextFields,
          },
          null,
          null
        )
      );
    }
    if (dispatch && saveWorkflow) {
      dispatch(saveWorkflow(1));
    }
    return nextFields;
  } finally {
    if (dispatch && loadingperform) dispatch(loadingperform(false));
  }
}
